import { publishCustomerSessionRefreshed } from "./customerSession.ts"
import { captureError } from "@otto-ec/global-resources/apm"
import { service, team } from "@kestrel/bundle-metadata"
import { randomUuid } from "./polyfills/randomUuid.ts"
import { requestLock } from "./polyfills/requestLock.ts"

const LOCK_NAME = 'fti-session-refresh-lock';
let refreshJobId: number | null = null;

class RequestIdStorage {
    private static readonly KEY = 'fti-session-refresh-request-id';

    get(): string {
        const stored = localStorage.getItem(RequestIdStorage.KEY);
        if (stored) {
            return stored;
        }
        let newId = randomUuid()
        localStorage.setItem(RequestIdStorage.KEY, newId);
        return newId;
    }

    clear(): void {
        localStorage.removeItem(RequestIdStorage.KEY);
    }
}

interface NextRefresh {
    refreshAfterTimestamp: number,
    extendExpiration: boolean
}

class NextRefreshScheduler {
    private static readonly KEY = "fti-session-next-refresh";

    nextDue(): NextRefresh | null {
        const storedNextRefresh = localStorage.getItem(NextRefreshScheduler.KEY)
        if (storedNextRefresh) {
            try {
                const nextRefresh = JSON.parse(storedNextRefresh) as NextRefresh;
                const now = Date.now();
                if (now >= nextRefresh.refreshAfterTimestamp) {
                    return nextRefresh;
                }
            } catch (e) {
                console.error("Error while parsing next refresh from localStorage. Clearing next refresh.", e);
                this.clear()
            }
        }
        return null;
    }

    schedule(seconds: number, extendExpiration: boolean): void {
        const storedRefreshAfter = localStorage.getItem(NextRefreshScheduler.KEY)
        const currentNextRefresh = storedRefreshAfter ? JSON.parse(storedRefreshAfter) as NextRefresh : null;
        const now = Date.now();
        const requestedNextRefresh = now + (seconds * 1000);
        const newNextRefresh: NextRefresh = {
            refreshAfterTimestamp: Math.min(requestedNextRefresh, currentNextRefresh?.refreshAfterTimestamp ?? requestedNextRefresh),
            extendExpiration: extendExpiration || (currentNextRefresh?.extendExpiration ?? false)
        }
        localStorage.setItem(NextRefreshScheduler.KEY, JSON.stringify(newNextRefresh));
    }

    clear(): void {
        localStorage.removeItem(NextRefreshScheduler.KEY);
    }

}

const nextRequestScheduler = new NextRefreshScheduler();
const requestIdStorage = new RequestIdStorage();

interface RefreshResult {
    refreshAfterSeconds: number | null,
    hasCustomerSession: boolean
}

async function refreshSessionByOrt(extendExpiration: boolean): Promise<RefreshResult> {
    const requestId = requestIdStorage.get();
    const url = `/session-maintenance/session/refresh?requestId=${requestId}&extendExpiration=${extendExpiration}`;

    const response = await fetch(url);

    if (response.ok) {
        requestIdStorage.clear();
        const hasCustomerSession = response.headers.get('Has-Customer-Session') == "true";
        const refreshAfter = response.headers.get('Refresh-After');
        const refreshAfterSeconds = refreshAfter ? parseInt(refreshAfter, 10) : null;
        return {
            refreshAfterSeconds,
            hasCustomerSession
        }
    } else {
        throw new Error(`Failed to refresh session: ${response.status} ${response.statusText}`);
    }
}

async function refreshIfNeeded() {
    const nextDue = nextRequestScheduler.nextDue()
    if (nextDue != null) {
        await requestLock(LOCK_NAME, async () => {
            const nextDueInsideLock = nextRequestScheduler.nextDue();
            if (nextDueInsideLock != null) {
                nextRequestScheduler.clear()
                try {
                    const refreshResult = await refreshSessionByOrt(nextDueInsideLock.extendExpiration);
                    publishCustomerSessionRefreshed({ hasCustomerSession: refreshResult.hasCustomerSession })
                    if (refreshResult?.refreshAfterSeconds != null) {
                        nextRequestScheduler.schedule(refreshResult.refreshAfterSeconds, false);
                    }
                } catch (e) {
                    if(navigator.onLine) {
                        captureError(
                            e as Error,
                            { service, team },
                            { type: "session-refresh-request-by-ort-failed" },
                        );
                    }
                    nextRequestScheduler.schedule(5, nextDueInsideLock.extendExpiration);
                }
            }
        });
    }
}

async function checkRefreshNeededPeriodically() {
    await refreshIfNeeded()
    /*                                   */
    refreshJobId = window.setTimeout(checkRefreshNeededPeriodically, 1000)
}

export async function startRefreshByOrt() {
    await requestLock(LOCK_NAME, () => nextRequestScheduler.schedule(0, true));
    await checkRefreshNeededPeriodically()
}

export function stopRefreshByOrt() {
    if (refreshJobId != null) {
        window.clearTimeout(refreshJobId)
        refreshJobId = null
    }
}


